/**
 * Zero-Knowledge Vault — Crypto-Primitives.
 *
 * Architektur:
 * - KEK (Key Encryption Key) wird aus Passphrase via PBKDF2-SHA256 abgeleitet
 *   (~600k Iterationen — empfohlen von OWASP 2023+).
 * - Pro Secret: random DEK (Data Encryption Key, 32 bytes), wird mit KEK gewrappt.
 * - Body wird mit DEK verschlüsselt.
 * - Cipher: AES-256-GCM (authenticated encryption, IV 12 bytes random pro op).
 *
 * Server sieht nur Ciphertexts + KDF-Parameter, niemals KEK/DEK/Passphrase/Klartext.
 */

const VERIFIER_MAGIC = "thunderbrain-vault-v1";
const DEFAULT_ITERATIONS = 600_000;

export interface VaultMeta {
  initialized: boolean;
  kdf_algo: string;
  kdf_iterations: number;
  kdf_salt: string;
  verifier: string;
  verifier_iv: string;
}

export interface VaultInitPayload {
  kdf_algo: string;
  kdf_iterations: number;
  kdf_salt: string;
  verifier: string;
  verifier_iv: string;
}

export interface EncryptedBundle {
  encrypted_dek: string;
  encrypted_dek_iv: string;
  encrypted_body: string;
  encrypted_body_iv: string;
}

// ----- Base64 helpers -----

export function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}

export function b64decode(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s);
  const buf = new ArrayBuffer(bin.length);
  const u8 = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

// ----- Random helpers -----

function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const buf = new ArrayBuffer(n);
  const u8 = new Uint8Array(buf);
  crypto.getRandomValues(u8);
  return u8;
}

// ----- KDF: Passphrase → KEK -----

async function importPassphrase(passphrase: string): Promise<CryptoKey> {
  const bytes = new TextEncoder().encode(passphrase);
  return crypto.subtle.importKey("raw", toBuf(bytes), "PBKDF2", false, ["deriveKey"]);
}

export async function deriveKek(
  passphrase: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number = DEFAULT_ITERATIONS,
): Promise<CryptoKey> {
  const base = await importPassphrase(passphrase);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// ----- AES-GCM primitives -----

function toBuf(u8: Uint8Array): ArrayBuffer {
  // Force an ArrayBuffer (not SharedArrayBuffer) view for WebCrypto.
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}

async function aesEncrypt(
  key: CryptoKey,
  plain: Uint8Array,
): Promise<{ ct: Uint8Array<ArrayBuffer>; iv: Uint8Array<ArrayBuffer> }> {
  const iv = randomBytes(12);
  const ctBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toBuf(iv) },
    key,
    toBuf(plain),
  );
  return { ct: new Uint8Array(ctBuf), iv };
}

async function aesDecrypt(
  key: CryptoKey,
  ct: Uint8Array,
  iv: Uint8Array,
): Promise<Uint8Array<ArrayBuffer>> {
  const plainBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toBuf(iv) },
    key,
    toBuf(ct),
  );
  return new Uint8Array(plainBuf);
}

// ----- Verifier (proof-of-passphrase ohne Server-Roundtrip) -----

export async function makeVerifier(kek: CryptoKey): Promise<{ verifier: string; verifier_iv: string }> {
  const { ct, iv } = await aesEncrypt(kek, new TextEncoder().encode(VERIFIER_MAGIC));
  return { verifier: b64encode(ct), verifier_iv: b64encode(iv) };
}

export async function checkVerifier(
  kek: CryptoKey,
  verifierB64: string,
  ivB64: string,
): Promise<boolean> {
  try {
    const plain = await aesDecrypt(kek, b64decode(verifierB64), b64decode(ivB64));
    return new TextDecoder().decode(plain) === VERIFIER_MAGIC;
  } catch {
    return false;
  }
}

// ----- Vault setup -----

export async function buildInitPayload(passphrase: string): Promise<{
  kek: CryptoKey;
  payload: VaultInitPayload;
}> {
  const salt = randomBytes(16);
  const iterations = DEFAULT_ITERATIONS;
  const kek = await deriveKek(passphrase, salt, iterations);
  const { verifier, verifier_iv } = await makeVerifier(kek);
  return {
    kek,
    payload: {
      kdf_algo: "PBKDF2-SHA256",
      kdf_iterations: iterations,
      kdf_salt: b64encode(salt),
      verifier,
      verifier_iv,
    },
  };
}

export async function unlockVault(meta: VaultMeta, passphrase: string): Promise<CryptoKey | null> {
  const kek = await deriveKek(passphrase, b64decode(meta.kdf_salt), meta.kdf_iterations);
  const ok = await checkVerifier(kek, meta.verifier, meta.verifier_iv);
  return ok ? kek : null;
}

// ----- Secret encryption -----

export async function encryptSecret(kek: CryptoKey, plaintext: string): Promise<EncryptedBundle> {
  const dekRaw = randomBytes(32);
  const wrapped = await aesEncrypt(kek, dekRaw);
  const dekKey = await crypto.subtle.importKey(
    "raw",
    toBuf(dekRaw),
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  const body = await aesEncrypt(dekKey, new TextEncoder().encode(plaintext));
  dekRaw.fill(0);
  return {
    encrypted_dek: b64encode(wrapped.ct),
    encrypted_dek_iv: b64encode(wrapped.iv),
    encrypted_body: b64encode(body.ct),
    encrypted_body_iv: b64encode(body.iv),
  };
}

export async function decryptSecret(kek: CryptoKey, bundle: EncryptedBundle): Promise<string> {
  const dekRaw = await aesDecrypt(
    kek,
    b64decode(bundle.encrypted_dek),
    b64decode(bundle.encrypted_dek_iv),
  );
  const dekKey = await crypto.subtle.importKey(
    "raw",
    toBuf(dekRaw),
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"],
  );
  const plain = await aesDecrypt(
    dekKey,
    b64decode(bundle.encrypted_body),
    b64decode(bundle.encrypted_body_iv),
  );
  dekRaw.fill(0);
  return new TextDecoder().decode(plain);
}

// ----- Capability check -----

export function isCryptoAvailable(): boolean {
  return (
    typeof crypto !== "undefined" &&
    typeof crypto.subtle !== "undefined" &&
    window.isSecureContext === true
  );
}
